<template>
  <div class="landing-page">
    <!-- Each block is revealed as it scrolls into view (see mounted). Without JS or with reduced motion everything is simply visible. -->
    <section class="block hero">
      <p class="eyebrow">{{ $t('landing.eyebrow') }}</p>
      <h1>
        <span class="headline">{{ $t('landing.headlinePre') }}</span>
        <span class="headline-bold">{{ $t('landing.headlineBold') }}</span><span class="headline">{{ $t('landing.headlinePost') }}</span>
      </h1>
      <a
        href="https://www.producthunt.com/posts/smoothride-infrastructuregpt?utm_source=badge-featured&utm_medium=badge&utm_souce=badge-smoothride&#0045;infrastructuregpt"
        target="_blank" rel="noopener"
        ><img
          src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=401591&theme=dark"
          alt="SmoothRide&#0032;&#0047;&#0032;InfrastructureGPT - Citizen&#0032;x&#0032;AI&#0058;&#0032;Together&#0032;towards&#0032;better&#0032;infrastructure | Product Hunt"
          style="width: 200px; height: 54px"
          width="200"
          height="54"
      /></a>
      <div class="btn-wrapper">
        <router-link :to="$lp('/about')" :aria-label="$t('common.nav.about')">
          <button class="btn-round" type="button" tabindex="-1"><i class="fas fa-info" aria-hidden="true"></i></button>
        </router-link>
        <button class="btn-cta" type="button" @click="scrollToNext">
          <i class="fas fa-bicycle" aria-hidden="true"></i> {{ $t('landing.getStarted') }}
        </button>
      </div>
    </section>

    <section class="block block--md" id="share">
      <h2>
        <span class="headline-bold">{{ $t('landing.shareBold') }}</span>
        <span class="headline">{{ $t('landing.shareRest') }}</span>
        <span class="headline-bold"> 🚲</span>
      </h2>
      <TopScene class="landing-graphic" />
      <div class="btn-wrapper">
        <router-link :to="$lp('/explore')">
          <button class="btn-round" type="button" tabindex="-1" :aria-label="$t('common.nav.explore')"><i class="fas fa-map" aria-hidden="true"></i></button>
        </router-link>
        <router-link :to="$lp('/submit')">
          <button class="btn-cta" type="button"><i class="fas fa-camera" aria-hidden="true"></i> {{ $t('landing.submitReport') }}</button>
        </router-link>
      </div>
    </section>

    <section class="block block--xl">
      <h2>
        <span class="headline-bold">{{ $t('landing.ideateBold') }}</span>
        <span class="headline">{{ $t('landing.ideateRest') }}</span>
        <span class="headline-bold"> 🧑🏽🤝🤖</span>
      </h2>
      <AdviceSection :h2="$t('landing.adviceTitle')" subtitle="" :ratings="[]" :result="advice_array" @update:result="result = $event" />
    </section>

    <section class="block block--md">
      <h2>
        <span class="headline-bold">{{ $t('landing.inspiredBold') }}</span>
        <span class="headline">{{ $t('landing.inspiredRest') }}</span>
      </h2>
      <AdviceSection :h2="$t('landing.contentTitle')" subtitle="" :ratings="[]" :result="content_array" @update:result="result = $event" />
      <div class="btn-wrapper">
        <router-link :to="$lp('/submit')">
          <button class="btn-round" type="button" tabindex="-1" :aria-label="$t('common.nav.submit')"><i class="fas fa-camera" aria-hidden="true"></i></button>
        </router-link>
        <router-link :to="$lp('/explore')">
          <button class="btn-cta" type="button"><i class="fas fa-map" aria-hidden="true"></i> {{ $t('landing.exploreMore') }}</button>
        </router-link>
      </div>
    </section>

    <section class="block">
      <h2>
        <span class="headline-bold">{{ $t('landing.communityBold') }}</span>
        <span class="headline">{{ $t('landing.communityRest') }}</span>
        <span class="headline-bold"> 🤲🚴🏽‍♀️</span>
      </h2>
      <ul class="custom-list">
        <li>{{ $t('landing.point1') }}</li>
        <li>{{ $t('landing.point2') }}</li>
        <li>{{ $t('landing.point3') }}</li>
      </ul>
      <LoopScene class="landing-graphic" />
    </section>

    <section class="block block--md">
      <FaqSection />
    </section>

    <section class="block closing">
      <h2>
        <span class="headline-bold">{{ $t('landing.startBold') }}</span>
        <span class="headline">{{ $t('landing.startRest') }} 🚴🏽‍♀️🚀</span>
      </h2>
      <div class="btn-wrapper">
        <router-link :to="$lp('/explore')">
          <button class="btn-round" type="button" tabindex="-1" :aria-label="$t('common.nav.explore')"><i class="fas fa-map" aria-hidden="true"></i></button>
        </router-link>
        <router-link :to="$lp('/submit')">
          <button class="btn-cta" type="button"><i class="fas fa-camera" aria-hidden="true"></i> {{ $t('landing.submitReport') }}</button>
        </router-link>
      </div>
      <HillScene class="landing-graphic" />
    </section>
  </div>
</template>

<script>
import AdviceSection from "@/components/AdviceSection.vue";
import FaqSection from "@/components/FaqSection.vue";
import HillScene from "@/components/HillScene.vue";
import TopScene from "@/components/scenes/TopScene.vue";
import LoopScene from "@/components/scenes/LoopScene.vue";
export default {
  name: "LandingPage",
  components: {
    HillScene,
    TopScene,
    LoopScene,
    AdviceSection,
    FaqSection,
  },
  mounted() {
    // Scrollytelling: blocks (and their children, one after another) fade in as they enter the viewport.
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || !("IntersectionObserver" in window)) return;
    const blocks = this.$el.querySelectorAll(".block");
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            this.observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    );
    this.$el.classList.add("reveal-ready");
    blocks.forEach((b) => this.observer.observe(b));
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    scrollToNext() {
      const next = this.$el.querySelector("#share");
      if (next) next.scrollIntoView({ behavior: "smooth", block: "start" });
    },
  },
  data() {
    return {
      advice_array: [
        {
          advice:
            "Create a curb extension by temporarily adding a barrier that directs the water to the section between the parked cars and the bike lane. Add a few temporary planters on the barrier and surround them with chairs for people to rest.",
          category: "Fast to achieve",
          record_id: "4xgdknnf3yyso3f",
        },
        {
          advice:
            "Install a curb cut or permeable pavement in the affected area allowing water to drain. Additionally, consider installing bike shelters to protect cyclists from inclement weather.",
          category: "Best practice",
          record_id: "39nh4rav24qneow",
        },
        {
          advice:
            "Install temporary trampolines in front of traffic lights to allow bicyclists to safely wait out the rain. The trampolines could also generate electricity for the city's grid.",
          category: "Super creative",
          record_id: "0isry4ossa006yc",
        },
      ],
      content_array: [
        {
          video_id: "z75fqfp37KI",
        },
        {
          video_id: "EHwCYnBxYWQ",
        },
        {
          video_id: "kgdyq6ol-OM",
        },
      ],
    };
  },
};
</script>

<style scoped>
.landing-page h1,
.landing-page h2 {
  max-width: 600px;
  line-height: 1.2;
}

/* Fresh Mint: small pill above the headline */
.eyebrow {
  margin: 0;
  padding: 6px 12px;
  border-radius: var(--radius-pill);
  background: var(--color-subtle);
  color: var(--color-primary-text);
  font-size: 12px;
  font-weight: 500;
}
ul,
li {
  list-style: inside;
  text-align: center;
  padding: 0;
  margin: 0;
}

li {
  padding: 10px 0;
}

li:last-child {
  border-bottom: none;
}

/* Desktop styles */
@media (min-width: 1025px) {
  .headline {
    font-size: 44px;
    font-family: Poppins;
    font-weight: 300;
    word-wrap: break-word;
  }

  .headline-bold {
    font-size: 44px;
    font-family: Poppins;
    font-weight: 600;
    word-wrap: break-word;
  }
}
.landing-page {
  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: center;
  /* generous air between the content blocks */
  gap: clamp(72px, 14vh, 144px);
  margin: 0 16px;
  padding: clamp(16px, 4vh, 48px) 0 0; /* the closing block sits flush with the bottom of the page */
}

.block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(24px, 4vh, 40px);
  width: 100%;
  max-width: var(--block-w, var(--w-md));
}
.hero { min-height: 70vh; justify-content: center; }
.closing { padding-bottom: 0; }
.closing .landing-graphic { display: block; margin-bottom: 0; max-width: 600px; width: 100vw; }

/* Reveal on scroll: only active once JS has marked the page as ready */
.reveal-ready .block > * {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.2, 0.7, 0.2, 1);
}
.reveal-ready .block.is-visible > * { opacity: 1; transform: none; }
.reveal-ready .block.is-visible > *:nth-child(2) { transition-delay: 0.12s; }
.reveal-ready .block.is-visible > *:nth-child(3) { transition-delay: 0.24s; }
.reveal-ready .block.is-visible > *:nth-child(4) { transition-delay: 0.36s; }
.reveal-ready .block.is-visible > *:nth-child(n+5) { transition-delay: 0.48s; }
@media (prefers-reduced-motion: reduce) {
  .reveal-ready .block > * { opacity: 1; transform: none; transition: none; }
}

.landing-page img {
  /* width: 80%; */
  max-width: 600px;
  width: 100vw;
}

.advice-section { width: 100%; max-width: 100%; }

.btn-wrapper {
  display: flex;
  gap: 8px;
  flex-direction: row;
}

/* Tablet styles */
@media (min-width: 601px) and (max-width: 1024px) {
  .headline {
    font-size: 32px;
    font-family: Poppins;
    word-wrap: break-word;
    font-weight: 400;
  }

  .headline-bold {
    font-size: 32px;
    font-family: Poppins;
    font-weight: 600;
    word-wrap: break-word;
  }
}

/* Mobile styles */
@media (max-width: 600px) {
  .headline {
    font-size: 32px;
    font-family: Poppins;
    font-weight: 400;
    word-wrap: break-word;
  }

  .headline-bold {
    font-size: 32px;
    font-family: Poppins;
    font-weight: 600;
    word-wrap: break-word;
  }

}
</style>
